import { useCallback, useEffect, useState } from "react";
import { api, jfetch, type QueueItemView } from "../api";

/**
 * CP-13 — Resource Governor UI: live Kapazitäten, aktive Grants und die
 * exakte Blockade je WAIT_RESOURCE-Item („welche Kapazität/grant blockiert"),
 * alles über die bestehenden Leseflächen — Buchungen entstehen weiterhin
 * AUSSCHLIESSLICH durch Governor/Supervisor gegen die DB, nie per UI.
 */

interface ResourcesResponse {
  ok: boolean;
  classes: Array<{ classId: string; scopeKind: string; capacity: number; active: number }>;
  grants: Array<{
    grant_id: string;
    class_id: string;
    scope_key: string;
    workitem_id: string;
    lease_id: string | null;
    granted_at: string;
    expires_at: string;
    released_at: string | null;
  }>;
}

export function ResourceGovernorPanel(): JSX.Element {
  const [resources, setResources] = useState<ResourcesResponse | null>(null);
  const [waitResource, setWaitResource] = useState<QueueItemView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((): void => {
    setLoading(true);
    setError(null);
    Promise.all([
      jfetch<ResourcesResponse>("/api/resources"),
      api.controlPlaneQueue().catch(() => null),
    ])
      .then(([r, q]) => {
        setResources(r);
        setWaitResource((q?.items ?? []).filter((it) => it.queueState === "WAIT_RESOURCE"));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="space-y-3" aria-label="Ressourcen" data-testid="resource-governor-panel">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Ressourcen (Governor)</h2>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? "Lade…" : "Aktualisieren"}
        </button>
      </div>

      {error && (
        <div className="card p-4 text-rose-300 text-sm" role="alert">
          Ressourcen nicht abrufbar: {error}
        </div>
      )}

      {resources && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {resources.classes.map((k) => {
            const ausgelastet = k.active >= k.capacity;
            const prozent = k.capacity > 0 ? Math.min(100, Math.round((k.active / k.capacity) * 100)) : 0;
            return (
              <div
                key={k.classId}
                className={`card p-3 ${ausgelastet ? "border-amber-500/40" : ""}`}
                data-testid={`resource-class-${k.classId}`}
              >
                <div className="text-[10px] text-jarvis-muted uppercase tracking-wider truncate font-mono" title={k.classId}>
                  {k.classId}
                </div>
                <div className={`text-2xl font-semibold ${ausgelastet ? "text-amber-300" : ""}`}>
                  {k.active} / {k.capacity}
                </div>
                <div className="mt-1.5 h-1.5 rounded bg-jarvis-border/50 overflow-hidden">
                  <div
                    className={`h-full ${ausgelastet ? "bg-amber-400" : "bg-emerald-400"}`}
                    style={{ width: `${prozent}%` }}
                  />
                </div>
                <div className="text-[10px] text-jarvis-muted mt-1 font-mono">{k.scopeKind}</div>
              </div>
            );
          })}
        </div>
      )}

      {resources && (
        <div className="card p-3 sm:p-4">
          <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
            Aktive Grants ({resources.grants.length})
          </h3>
          {resources.grants.length === 0 ? (
            <div className="text-sm text-jarvis-muted">Keine aktiven Grants — alle Kapazitäten frei.</div>
          ) : (
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Ressourcen-Tabelle">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-jarvis-muted border-b border-jarvis-border">
                    <th className="p-2">Klasse</th>
                    <th className="p-2">Scope</th>
                    <th className="p-2">Workitem</th>
                    <th className="p-2">seit</th>
                    <th className="p-2">bis</th>
                  </tr>
                </thead>
                <tbody>
                  {resources.grants.map((g) => (
                    <tr key={g.grant_id} className="border-b border-jarvis-border/50 last:border-0" data-testid="active-grant-row">
                      <td className="p-2 font-mono">{g.class_id}</td>
                      <td className="p-2 font-mono truncate max-w-[10rem]" title={g.scope_key}>
                        {g.scope_key}
                      </td>
                      <td className="p-2 font-mono">{g.workitem_id}</td>
                      <td className="p-2 whitespace-nowrap">{zeitKurz(g.granted_at)}</td>
                      <td className="p-2 whitespace-nowrap">{zeitKurz(g.expires_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="card p-3 sm:p-4">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
          Wartet auf Ressource ({waitResource.length})
        </h3>
        {waitResource.length === 0 ? (
          <div className="text-sm text-jarvis-muted">Kein Item wartet auf Kapazität.</div>
        ) : (
          <div className="space-y-2">
            {waitResource.map((it) => (
              <div key={it.id} className="flex items-start justify-between gap-3 border-b border-jarvis-border/50 last:border-0 pb-2 last:pb-0" data-testid={`wait-resource-${it.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{it.title}</div>
                  <div className="font-mono text-[10px] text-jarvis-muted">{it.id}</div>
                </div>
                <div className="text-xs text-amber-200/90 text-right max-w-[14rem]">
                  Blockiert durch: {it.queueStateReason ?? "Kapazität erschöpft"}
                  {it.writerScope && <div className="font-mono text-[10px] text-jarvis-muted">{it.writerScope}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/** OWNEXP Review: nie „Invalid Date" im Owner-View — fehlende/unlesbare
 *  Zeitangaben zeigen ehrlich „—", statt einen Rohfehler zu rendern. */
function zeitKurz(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("de-DE");
}
